import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {FormProps, Input} from 'antd';
import {Form, notification} from 'antd';
import {produce} from 'immer';
import {PhoneInput} from '@/components/PhoneInput/PhoneInput';
import catalogueService from '@/services/catalogueService';
import {ModeChoice} from './ModeChoice';
import {OtpStep} from './OtpStep';
import {StepRow} from './StepRow';
import {FormFieldRow} from './FormFieldRow';

type ModalMode = 'login' | 'register' | 'both';

type PersonData = {
    lastName?: string;
    firstName?: string;
    patronymic?: string;
};

type LoginOrRegistrationModalProps = {
    mode: ModalMode;
    onHide: () => void;
    onFinish: (token: string) => void;
};

const OTP_TIMEOUT = 60;

export const LoginOrRegistrationModalContent: React.FC<LoginOrRegistrationModalProps> = ({ mode, onHide, onFinish }) => {
    const [form] = Form.useForm<PersonData>();

    const [currentMode, setCurrentMode] = useState<ModalMode>(mode);
    const [otpSent, setOtpSent] = useState(false);
    const [otpSendInProgress, setOtpSendInProgress] = useState(false);
    const [otpUnlocked, setOtpUnlocked] = useState(true);
    const [otpCooldown, setOtpCooldown] = useState(0);
    const [authSendInProgress, setAuthSendInProgress] = useState(false);
    const [phoneValue, setPhoneValue] = useState<string>();
    const [enteredOtp, setEnteredOtp] = useState<string>();
    const [autoOtpCheckAlreadyInitiated, setAutoOtpCheckAlreadyInitiated] = useState(false);
    const [personData, setPersonData] = useState<PersonData>();

    const personDataRef = useRef<PersonData | undefined>(personData);

    useEffect(() => {
        personDataRef.current = personData;
    }, [personData]);

    useEffect(() => {
        if (otpCooldown <= 0) return;

        const interval = window.setInterval(() => {
            setOtpCooldown((prev) => {
                if (prev <= 1) {
                    window.clearInterval(interval);
                    setOtpUnlocked(true);
                    return 0;
                }

                return prev - 1;
            });
        }, 1000);

        return () => window.clearInterval(interval);
    }, [otpCooldown]);

    const resetState = useCallback(() => {
        setPhoneValue("+380");
        setOtpSent(false);
        setOtpSendInProgress(false);
        setOtpUnlocked(true);
        setOtpCooldown(0);
        setEnteredOtp(undefined);
        setAuthSendInProgress(false);
        setPersonData(undefined);
        setAutoOtpCheckAlreadyInitiated(false);
        personDataRef.current = undefined;
        form.resetFields();
    }, [form]);

    const onModalHide = useCallback(() => {
        resetState();
        onHide();
    }, [onHide, resetState]);

    useEffect(() => {
        return () => {
            resetState();
        };
    }, [resetState]);

    const onPersonFieldChanged = useCallback((field: keyof PersonData, value: string) => {
        const nextState = produce(personDataRef.current, (draft: PersonData | undefined) => {
            if (draft) {
                draft[field] = value;
                return;
            }

            return { [field]: value } as PersonData;
        });

        setPersonData(nextState as PersonData);
        personDataRef.current = nextState as PersonData;
    }, []);

    const onSendOtpInLoginOrRegistrationModalHandler = useCallback(async () => {
        if (otpSendInProgress || !phoneValue || !otpUnlocked) return;

        setOtpSendInProgress(true);
        setOtpUnlocked(false);

        const sendRes = otpSent
            ? await catalogueService.resendOtp(phoneValue)
            : await catalogueService.sendOtp(phoneValue);

        setOtpSendInProgress(false);

        if ((sendRes?.code || '').toUpperCase() !== 'OK') {
            notification.warning({
                message: 'Помилка відправки ОТП',
                description: sendRes?.message || sendRes?.details,
            });
            setOtpUnlocked(true);
            return;
        }

        setOtpSent(true);
        setOtpCooldown(OTP_TIMEOUT);
        setAutoOtpCheckAlreadyInitiated(false);
    }, [otpSendInProgress, phoneValue, otpUnlocked, otpSent]);

    const onSendOtpAgainInLoginOrRegistrationModalHandler = useCallback(async () => {
        setEnteredOtp(undefined);
        setAutoOtpCheckAlreadyInitiated(false);
        await onSendOtpInLoginOrRegistrationModalHandler();
    }, [onSendOtpInLoginOrRegistrationModalHandler]);

    const onLoginOtpInLoginOrRegistrationHandler = useCallback(
        async (otpToCheck?: string) => {
            if (authSendInProgress || !phoneValue) return;

            const otpToCheckToUse = otpToCheck || enteredOtp;
            if (!otpToCheckToUse) return;

            setAuthSendInProgress(true);
            notification.destroy();

            const checkRes = await catalogueService.verifyOtp(
                currentMode === 'login',
                phoneValue,
                otpToCheckToUse,
                personDataRef.current?.lastName,
                personDataRef.current?.firstName,
                personDataRef.current?.patronymic,
            );

            if ((checkRes?.code || '').toUpperCase() === 'OK') {
                onFinish(checkRes?.token || '');
            } else {
                notification.warning({
                    message: 'Введений ОТП невірний!',
                    description: checkRes?.message || checkRes?.details,
                    duration: 0,
                });
            }

            setAuthSendInProgress(false);
        },
        [authSendInProgress, currentMode, enteredOtp, onFinish, phoneValue],
    );

    const onReturnToInputtingButtonClicked = useCallback(() => {
        setOtpSent(false);
        setEnteredOtp(undefined);
        setOtpUnlocked(true);
        setOtpCooldown(0);
        setAutoOtpCheckAlreadyInitiated(false);
    }, []);

    const handlePhoneChanged = useCallback((newValue?: string) => {
        setPhoneValue(newValue);
    }, []);

    const handleEnteredOtpChanged = useCallback(
        (newValue?: string) => {
            setEnteredOtp(newValue);

            if (newValue?.length === 4 && !autoOtpCheckAlreadyInitiated) {
                setAutoOtpCheckAlreadyInitiated(true);
                void onLoginOtpInLoginOrRegistrationHandler(newValue);
            }
        },
        [autoOtpCheckAlreadyInitiated, onLoginOtpInLoginOrRegistrationHandler],
    );

    // @ts-ignore
    const handleFormValuesChange: FormProps<PersonData>['onValuesChange'] = useCallback((changedValues, allValues) => {
            const [fieldName, fieldValue] = Object.entries(changedValues)[0] || [];
            if (fieldName) onPersonFieldChanged(fieldName as keyof PersonData, String(fieldValue ?? ''));
            setPersonData(allValues);
            personDataRef.current = allValues;
        },
        [onPersonFieldChanged],
    );

    const canRequestOtp = useMemo(() => {
        // console.log(`canRequestOtp: phoneValue-${phoneValue}, otpCooldown-${otpCooldown}, otpSendInProgress-${otpSendInProgress}, 
        // lastName-${personDataRef.current?.lastName}, firstName-${personDataRef.current?.firstName}, patronymic-${personDataRef.current?.patronymic}`);
        if (phoneValue?.length !== 13) return false;
        if (otpCooldown > 0 || otpSendInProgress) return false;

        if (currentMode === 'register') {
            const regex = /^[a-zа-яёіїєґ']+(?:[- ]+[a-zа-яёіїєґ']+)*$/ui;
            return Boolean(
                personDataRef.current?.lastName && regex.test(personDataRef.current?.lastName) &&
                personDataRef.current?.firstName && regex.test(personDataRef.current?.firstName) &&
                personDataRef.current?.patronymic && regex.test(personDataRef.current?.patronymic),
            );
        }

        return true;
    }, [currentMode, otpCooldown, otpSendInProgress, phoneValue, personDataRef.current]);

    const renderRegisterContent = () => (
        <div className="flex w-full flex-col gap-8 md:gap-10">
            <StepRow step={1} title="Введіть Ваші контактні дані">
                <Form
                    form={form}
                    layout="vertical"
                    requiredMark={false}
                    onValuesChange={handleFormValuesChange}
                    className="w-full"
                    disabled={otpSent || authSendInProgress}
                >
                    <div className="flex w-full flex-col gap-4">
                        <FormFieldRow label="Прізвище">
                            <Form.Item<PersonData> name="lastName" style={{height: '32px', borderRadius: 0, margin: 0}} 
                                                   rules={[{required: true, message:"Поле обов'язкове"}, {pattern:/^[a-zа-яёіїєґ']+(?:[- ]+[a-zа-яёіїєґ']+)*$/ui, message:"Не відповідає шаблону"}]}
                            >
                                <Input />
                            </Form.Item>
                        </FormFieldRow>

                        <FormFieldRow label="Ім'я">
                            <Form.Item<PersonData> name="firstName" style={{height: '32px', borderRadius: 0, margin: 0}}
                                                   rules={[{required: true, message:"Поле обов'язкове"}, {pattern:/^[a-zа-яёіїєґ']+(?:[- ]+[a-zа-яёіїєґ']+)*$/ui, message:"Не відповідає шаблону"}]}
                            >
                                <Input />
                            </Form.Item>
                        </FormFieldRow>

                        <FormFieldRow label="По батькові">
                            <Form.Item<PersonData> name="patronymic" style={{height: '32px', borderRadius: 0, margin: 0}}
                                                   rules={[{required: true, message:"Поле обов'язкове"}, {pattern:/^[a-zа-яёіїєґ']+(?:[- ]+[a-zа-яёіїєґ']+)*$/ui, message:"Не відповідає шаблону"}]}
                            >
                                <Input />
                            </Form.Item>
                        </FormFieldRow>
                    </div>
                </Form>
            </StepRow>

            <OtpStep
                currentMode="register"
                otpSent={otpSent}
                otpSendInProgress={otpSendInProgress}
                authSendInProgress={authSendInProgress}
                otpCooldown={otpCooldown}
                phoneValue={phoneValue}
                enteredOtp={enteredOtp}
                onSendOtp={async () => {
                    if (!canRequestOtp) {
                        notification.warning({
                            message: 'Введені дані не є вірними!',
                            description: "Перевірте правильність введених даних",
                        });
                        return;
                    }
                    await onSendOtpInLoginOrRegistrationModalHandler();
                }}
                onResendOtp={onSendOtpAgainInLoginOrRegistrationModalHandler}
                onSubmitOtp={() => void onLoginOtpInLoginOrRegistrationHandler()}
                onOtpChange={handleEnteredOtpChanged}
                onBackToPhoneEdit={onReturnToInputtingButtonClicked}
                handlePhoneChanged={handlePhoneChanged}
            />
        </div>
    );

    const renderLoginContent = () => (
        <OtpStep
            currentMode="login"
            otpSent={otpSent}
            otpSendInProgress={otpSendInProgress}
            authSendInProgress={authSendInProgress}
            otpCooldown={otpCooldown}
            phoneValue={phoneValue}
            enteredOtp={enteredOtp}
            onSendOtp={async () => {
                if (!canRequestOtp) {
                    notification.warning({
                        message: 'Введені дані не є вірними!',
                        description: "Перевірте правильність введених даних",
                    });
                    return;
                }
                await onSendOtpInLoginOrRegistrationModalHandler();
            }}
            onResendOtp={onSendOtpAgainInLoginOrRegistrationModalHandler}
            onSubmitOtp={() => void onLoginOtpInLoginOrRegistrationHandler()}
            onOtpChange={handleEnteredOtpChanged}
            onBackToPhoneEdit={onReturnToInputtingButtonClicked}
            handlePhoneChanged={handlePhoneChanged}
        />
    );

    const renderContentAccordingToMode = () => {
        switch (currentMode) {
            case 'login':
                return renderLoginContent();
            case 'register':
                return renderRegisterContent();
            case 'both':
                return <ModeChoice onLoginClick={() => setCurrentMode('login')} onRegisterClick={() => setCurrentMode('register')} />;
            default:
                return null;
        }
    };

    return (
        <div className="mx-auto flex w-full max-w-[760px] flex-col gap-10 px-4 pb-10 pt-12 md:gap-14 md:px-8">
            {renderContentAccordingToMode()}

            <button type="button" onClick={onModalHide} className="hidden">
                hidden reset trigger
            </button>
        </div>
    );
};